// Workshop: engine spec card with sources, modifications by category, live predicted range (spec §5.2).
import type { AppContext, ScreenHandle } from './app';
import type { ModSpec, SourceRef } from '../types';
import { modsFor, modById } from '../data/mods';
import { predictRange } from '../physics/predict';
import { computeBaseline } from '../sim/score';
import { h, clear, button, photo, link, cell } from './dom';
import { fmtNm, fmtHours, fmtCredits, fmtDelta, fmtPower, fmtThrust, fmtMass, fmtBsfc, fmtTsfc, fmtFt, fmtRpm, fmtFuel } from './format';
import { craftBuild, engineCount, fmtEfficiency, CATEGORY_LABEL, CATEGORY_ORDER, KIND_LABEL, rangeLabel, type CraftBuild } from './build';

export function mountWorkshop(root: HTMLElement, ctx: AppContext): ScreenHandle {
  const title = h('h1', { id: 'workshop-title' }, 'Workshop');
  const sub = h('p');
  const engineCol = h('div.panel');
  const modsCol = h('div.panel');
  const readoutCells = h('div.cells');
  const balance = h('span.badge.num', { 'aria-label': 'Credits balance' });
  const plan = button('Plan route', () => { if (ctx.session.craftId) ctx.go('briefing', { craftId: ctx.session.craftId }); }, { cls: 'btn btn-primary' });
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        button('← Hangar', () => ctx.go('hangar'), { cls: 'btn btn-quiet', ariaLabel: 'Back to the hangar' }),
        h('div.title', {}, title, sub),
        h('div.actions', {}, balance),
      ),
      h('div.two-col', {}, engineCol, modsCol),
      h('div.readout', { 'aria-live': 'polite' }, readoutCells, plan),
    ),
  );

  function sourceList(refs: SourceRef[]): HTMLElement {
    const seen = new Set<string>();
    const ul = h('ul.sources');
    for (const r of refs) {
      if (seen.has(r.url)) continue;
      seen.add(r.url);
      ul.append(h('li', {}, link(r.label, r.url)));
    }
    return ul;
  }

  function specCard(b: CraftBuild): void {
    const { craft, engine, rc } = b;
    clear(engineCol);
    engineCol.append(photo(`/img/engines/${engine.photo ?? engine.id}.webp`, engine.name, engine.name));
    engineCol.append(h('h2', { style: 'margin-top:.75rem' }, engine.name), h('p.muted', {}, `${KIND_LABEL[engine.kind]} · ${engineCount(craft) > 1 ? `${engineCount(craft)} engines` : 'one engine'}`));
    const rows: [string, string][] = [];
    if (engine.ratedThrustN) rows.push(['Dry thrust, sea level static', fmtThrust(engine.ratedThrustN)]);
    if (engine.afterburnerThrustN) rows.push(['Afterburner thrust', fmtThrust(engine.afterburnerThrustN)]);
    if (engine.ratedPowerKw) rows.push(['Rated power', `${fmtPower(engine.ratedPowerKw)} at ${fmtRpm(engine.ratedRpm)}`]);
    rows.push(['Dry mass', fmtMass(engine.dryMassKg)]);
    if (engine.bsfcKgPerKwh) rows.push(['Specific fuel consumption', fmtBsfc(engine.bsfcKgPerKwh)]);
    if (engine.tsfcKgPerNh) rows.push(['Cruise TSFC, installed', fmtTsfc(engine.tsfcKgPerNh)]);
    if (engine.tsfcAfterburnerKgPerNh) rows.push(['Afterburner TSFC', fmtTsfc(engine.tsfcAfterburnerKgPerNh)]);
    if (engine.criticalAltM) rows.push(['Critical altitude', fmtFt(engine.criticalAltM / 0.3048)]);
    if (rc.air && engine.criticalAltM !== undefined && rc.air.criticalAltM > engine.criticalAltM + 1) rows.push(['Critical altitude, your build', fmtFt(rc.air.criticalAltM / 0.3048)]);
    if (engine.propeller) rows.push(['Propeller', rc.air && rc.air.propeller !== engine.propeller ? `${engine.propeller} → ${rc.air.propeller} (mod)` : engine.propeller]);
    if (engine.kind === 'piston') rows.push(['Fuel system', engine.fuelInjected ? 'Fuel injection' : 'Carburettor']);
    rows.push(['Idle rpm', fmtRpm(engine.idleRpm)]);
    const cap = rc.air ? rc.air.fuelCapacityKg : rc.sea ? rc.sea.fuelCapacityKg : 0;
    const stockCap = craft.airframe?.fuelCapacityKg ?? craft.hull?.fuelCapacityKg ?? 0;
    rows.push(['Usable fuel', cap > stockCap + 0.5 ? `${fmtFuel(cap, craft.domain, b.big)} (stock ${fmtFuel(stockCap, craft.domain, b.big)})` : fmtFuel(cap, craft.domain, b.big)]);
    const dl = h('dl.spec');
    for (const [k, v] of rows) dl.append(h('dt', {}, k), h('dd', {}, v));
    engineCol.append(dl);
    if (engine.audio === 'verified' && engine.audioNote) engineCol.append(h('p.small.muted', { style: 'margin-top:.75rem' }, engine.audioNote));
    engineCol.append(h('details', {}, h('summary', {}, 'Sources'), sourceList([...engine.sources, ...craft.sources])));
  }

  function modRow(b: CraftBuild, mod: ModSpec, installed: string[]): HTMLElement {
    const isIn = installed.includes(mod.id);
    const missing = (mod.requires ?? []).filter((r) => !installed.includes(r)).map((r) => modById(r)?.name ?? r);
    const row = h('div.mod', { class: isIn ? 'mod-installed' : undefined });
    row.append(
      h('div.mod-head', {}, h('span.name', { style: 'font-weight:600' }, mod.name), h('span.cost', {}, fmtCredits(mod.costCredits))),
      h('p.mod-claim', {}, mod.claim),
      h('p.mod-src', {}, 'Sources: ', ...mod.sources.flatMap((s, i) => [i ? ', ' : '', link(s.label, s.url)])),
    );
    let btn: HTMLButtonElement;
    if (isIn) {
      btn = button('Remove', () => toggle(b, mod, false), { cls: 'btn', ariaLabel: `Remove ${mod.name}, refund ${fmtCredits(mod.costCredits)}` });
    } else if (missing.length) {
      btn = button('Install', () => undefined, { cls: 'btn', disabled: true, ariaLabel: `Install ${mod.name}; requires ${missing.join(', ')}` });
      row.append(h('p.small.muted', {}, `Requires ${missing.join(', ')}`));
    } else {
      const short = mod.costCredits - ctx.save.credits;
      btn = button('Install', () => toggle(b, mod, true), { cls: 'btn', disabled: short > 0, ariaLabel: `Install ${mod.name} for ${fmtCredits(mod.costCredits)}` });
      if (short > 0) row.append(h('p.small.muted', {}, `${fmtCredits(short)} short`));
    }
    row.append(btn);
    return row;
  }

  /** Install deducts the cost; remove refunds it and also removes (and refunds) anything that required it. */
  function toggle(b: CraftBuild, mod: ModSpec, install: boolean): void {
    const id = b.craft.id;
    let installed = ctx.save.builds[id] ?? [];
    if (install) {
      if (installed.includes(mod.id) || ctx.save.credits < mod.costCredits) return;
      ctx.save.credits -= mod.costCredits;
      installed = [...installed, mod.id];
    } else {
      const dependents = modsFor(id).filter((m) => installed.includes(m.id) && (m.requires ?? []).includes(mod.id));
      for (const d of [mod, ...dependents]) if (installed.includes(d.id)) ctx.save.credits += d.costCredits;
      const drop = new Set([mod.id, ...dependents.map((d) => d.id)]);
      installed = installed.filter((m) => !drop.has(m));
    }
    ctx.save.builds = { ...ctx.save.builds, [id]: installed };
    ctx.persist();
    render();
  }

  function modsPanel(b: CraftBuild): void {
    clear(modsCol);
    modsCol.append(h('h2', {}, 'Modifications'), h('p.muted.small', {}, 'Documented magnitudes with their sources; effects stack multiplicatively. Removing a mod refunds its cost.'));
    const installed = ctx.save.builds[b.craft.id] ?? [];
    const all = modsFor(b.craft.id);
    if (!all.length) { modsCol.append(h('p.state', {}, 'No modifications are documented for this craft yet.')); return; }
    for (const cat of CATEGORY_ORDER) {
      const group = all.filter((m) => m.category === cat);
      if (!group.length) continue;
      const box = h('div.mod-group', {}, h('h3', {}, CATEGORY_LABEL[cat]));
      for (const m of group) box.append(modRow(b, m, installed));
      modsCol.append(box);
    }
  }

  function readout(b: CraftBuild): void {
    clear(readoutCells);
    const base = computeBaseline(b.craft, b.engine);
    const r = predictRange(b.rc, b.craft.defaultCruise);
    const ratio = base.rangeNm > 0 ? r.rangeNm / base.rangeNm : 1;
    readoutCells.append(
      cell(`${rangeLabel(b.craft.domain)} · stock`, fmtNm(base.rangeNm)).root,
      cell('Your build', `${fmtNm(r.rangeNm)} (${fmtDelta(ratio)})`).root,
      cell('Endurance', fmtHours(r.hours)).root,
      cell('Efficiency', fmtEfficiency(r.efficiency, b.craft.domain, b.big)).root,
    );
  }

  function render(): void {
    const id = ctx.session.craftId;
    const b = id ? craftBuild(ctx.save, id) : null;
    if (!b) {
      sub.textContent = '';
      clear(engineCol); clear(modsCol); clear(readoutCells);
      engineCol.append(h('p.state', {}, 'No craft selected. Go back to the hangar and pick one.'));
      plan.disabled = true;
      return;
    }
    plan.disabled = false;
    title.textContent = `Workshop · ${b.craft.className}`;
    sub.textContent = `Modelled on ${b.craft.modelledOn}`;
    balance.textContent = fmtCredits(ctx.save.credits);
    specCard(b);
    modsPanel(b);
    readout(b);
  }

  return { show: render };
}
